import { worldToScreen } from "./coordinates.js";

const MIN_CUT_POSITION = 0.000001;

export function findNearestLineCut({ documentModel, screenPoint, viewport, tolerance = 8 }) {
  const dotsById = new Map(documentModel.dots.map((dot) => [dot.id, dot]));
  let nearestLine = null;

  for (const object of documentModel.objects) {
    if (object.type !== "line" || object.visible === false || object.locked === true) {
      continue;
    }

    const pointIds = Array.isArray(object.points) ? object.points.slice(0, 2) : [];
    const start = dotsById.get(pointIds[0]);
    const end = dotsById.get(pointIds[1]);

    if (!start || !end) {
      continue;
    }

    const screenStart = worldToScreen(start, viewport);
    const screenEnd = worldToScreen(end, viewport);
    const projection = projectPointToSegment(screenPoint, screenStart, screenEnd);

    if (projection.t <= MIN_CUT_POSITION || projection.t >= 1 - MIN_CUT_POSITION) {
      continue;
    }

    if (projection.distance > tolerance) {
      continue;
    }

    if (nearestLine && projection.distance >= nearestLine.distance) {
      continue;
    }

    nearestLine = {
      object,
      start,
      end,
      t: projection.t,
      distance: projection.distance,
      projectionScreen: projection.point,
      projectionWorld: interpolatePoint(start, end, projection.t)
    };
  }

  return nearestLine;
}

export function projectPointToSegment(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return {
      t: 0,
      point: { x: start.x, y: start.y },
      distance: Math.hypot(point.x - start.x, point.y - start.y)
    };
  }

  const rawT = ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared;
  const t = Math.min(1, Math.max(0, rawT));
  const projectedPoint = interpolatePoint(start, end, t);

  return {
    t,
    point: projectedPoint,
    distance: Math.hypot(point.x - projectedPoint.x, point.y - projectedPoint.y)
  };
}

function interpolatePoint(start, end, t) {
  return {
    x: start.x + (end.x - start.x) * t,
    y: start.y + (end.y - start.y) * t
  };
}
